import { useCallback, useEffect, useRef, useState } from 'react';
import {
  approveCatalogoSugestao,
  createCatalogoSugestao,
  createMunicipio,
  deleteMunicipio,
  exportCatalogo,
  fetchCatalogoHistory,
  fetchCatalogoStats,
  fetchCatalogoSugestoes,
  fetchMunicipios,
  importCatalogo,
  rejectCatalogoSugestao,
  updateMunicipio,
  type CatalogoHistoryItem,
  type CatalogoStats,
  type CatalogoSugestao,
  type Municipio,
} from '../services/api';
import { useT } from '../i18n';
// Issue #550: Page shell — padroniza o header da pagina (h1 + subtitulo).
import Page from '../components/Page';

// S10-T06 — Catálogo colaborativo de municípios/provedores (US-S10-006).
// Visão completa do catálogo: stats, filtros, import/export, moderação de
// sugestões e histórico.
// Decisão de produto (substitui #559): esta página é APENAS super_user —
// o guard por role está no App.tsx. Usuários de org usam a página
// "Solicitar provedor" (provedores suportados + sugestão + próprias
// solicitações); parceiro modera a fila de sugestões lá também.

const EMPTY_FORM = {
  codigo_ibge: '',
  nome: '',
  uf: '',
  provedor: '',
  agregador: '',
  justificativa: '',
};

// Issue #568: form do CRUD direto de municipio (sem justificativa — escrita
// imediata no catalogo global, endpoint /api/municipios, super_user only).
const EMPTY_MUN_FORM = {
  codigo_ibge: '',
  nome: '',
  uf: '',
  provedor: '',
  agregador: '',
};

export default function CatalogoMunicipios() {
  const t = useT();

  const [stats, setStats] = useState<CatalogoStats | null>(null);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [sugestoes, setSugestoes] = useState<CatalogoSugestao[]>([]);
  const [history, setHistory] = useState<CatalogoHistoryItem[]>([]);
  const [filters, setFilters] = useState({ uf: '', provedor: '', busca: '' });
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Issue #568: estado do CRUD direto de municipio (add/edit/delete).
  const [munForm, setMunForm] = useState(EMPTY_MUN_FORM);
  const [editingIbge, setEditingIbge] = useState<string | null>(null);
  const [showMunForm, setShowMunForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [s, m, sug] = await Promise.all([
        fetchCatalogoStats(),
        fetchMunicipios(filters.uf || filters.provedor || filters.busca ? filters : undefined),
        fetchCatalogoSugestoes(),
      ]);
      setStats(s);
      setMunicipios(m);
      setSugestoes(sug);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    fetchCatalogoHistory()
      .then(setHistory)
      .catch(() => setHistory([]));
  }, []);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    reload();
  };

  const submitSugestao = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      await createCatalogoSugestao({
        codigo_ibge: form.codigo_ibge.trim(),
        nome: form.nome.trim(),
        uf: form.uf.trim(),
        provedor: form.provedor.trim(),
        agregador: form.agregador.trim() || null,
        justificativa: form.justificativa.trim() || null,
      });
      setForm(EMPTY_FORM);
      setSuccess(t('catalogo.suggest.success'));
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    }
  };

  const handleApprove = async (id: string) => {
    setError(null);
    try {
      await approveCatalogoSugestao(id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    }
  };

  const handleReject = async (id: string) => {
    const note = window.prompt(t('catalogo.moderation.rejectPrompt')) ?? undefined;
    setError(null);
    try {
      await rejectCatalogoSugestao(id, note);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    }
  };

  const handleExport = async () => {
    try {
      const data = await exportCatalogo();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'catalogo-municipios.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setImportResult(null);
    try {
      const parsed = JSON.parse(await file.text()) as { municipios?: Omit<Municipio, 'id'>[] };
      const items = Array.isArray(parsed) ? parsed : parsed.municipios;
      if (!Array.isArray(items) || items.length === 0) {
        setError(t('catalogo.error.generic'));
        return;
      }
      const result = await importCatalogo(items);
      setImportResult(
        t('catalogo.import.result', {
          created: result.created,
          updated: result.updated,
          total: result.total,
        }),
      );
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    }
  };

  // Issue #568 — CRUD direto de municipio (restaura as acoes de escrita do
  // catalogo para super_user; o backend registra history em cada mutacao,
  // entao o historico e refeetchado apos save/delete).
  const refreshHistory = useCallback(() => {
    fetchCatalogoHistory()
      .then(setHistory)
      .catch(() => setHistory([]));
  }, []);

  const startEditMunicipio = (m: Municipio) => {
    setEditingIbge(m.codigo_ibge);
    setMunForm({
      codigo_ibge: m.codigo_ibge,
      nome: m.nome,
      uf: m.uf,
      provedor: m.provedor,
      agregador: m.agregador ?? '',
    });
    setShowMunForm(true);
  };

  const handleSaveMunicipio = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        nome: munForm.nome.trim(),
        uf: munForm.uf.trim().toUpperCase(),
        provedor: munForm.provedor.trim(),
        agregador: munForm.agregador.trim() || null,
      };
      if (editingIbge) {
        await updateMunicipio(editingIbge, payload);
      } else {
        await createMunicipio({ codigo_ibge: munForm.codigo_ibge.trim(), ...payload });
      }
      setShowMunForm(false);
      setEditingIbge(null);
      setMunForm(EMPTY_MUN_FORM);
      setSuccess(t('settings.providers.saved'));
      refreshHistory();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteMunicipio = async (m: Municipio) => {
    if (!window.confirm(t('settings.providers.deleteConfirm', { name: m.nome }))) return;
    setError(null);
    setSuccess(null);
    try {
      await deleteMunicipio(m.codigo_ibge);
      setSuccess(t('settings.providers.deleted'));
      refreshHistory();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    }
  };

  const canSaveMunicipio =
    munForm.nome.trim() &&
    munForm.uf.trim().length === 2 &&
    munForm.provedor.trim() &&
    (editingIbge !== null || munForm.codigo_ibge.trim().length === 7);

  if (loading && !stats) return <p className="text-brand-600">{t('app.loading')}</p>;

  const inputCls =
    'rounded border border-brand-300 px-2 py-1 text-sm text-brand-800 focus:border-brand-500 focus:outline-none';
  const thCls = 'py-2 pr-4 text-left text-xs font-semibold text-brand-500';
  const tdCls = 'py-2 pr-4 text-sm text-brand-800';

  return (
    <Page title={t('catalogo.title')} subtitle={t('catalogo.subtitle')}>
      {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && <p className="rounded bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>}
      {importResult && (
        <p className="rounded bg-green-50 px-3 py-2 text-sm text-green-700">{importResult}</p>
      )}

      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg border border-brand-200 bg-white p-4">
            <p className="text-2xl font-bold text-brand-800">{stats.municipios_cobertos}</p>
            <p className="text-xs text-brand-500">{t('catalogo.stats.municipios')}</p>
          </div>
          <div className="rounded-lg border border-brand-200 bg-white p-4">
            <p className="text-2xl font-bold text-brand-800">{stats.provedores_suportados}</p>
            <p className="text-xs text-brand-500">{t('catalogo.stats.provedores')}</p>
          </div>
          <div className="rounded-lg border border-brand-200 bg-white p-4">
            <p className="text-2xl font-bold text-brand-800">{stats.cobertura_por_uf.length}</p>
            <p className="text-xs text-brand-500">{t('catalogo.stats.ufs')}</p>
          </div>
          <div className="rounded-lg border border-brand-200 bg-white p-4">
            <p className="text-2xl font-bold text-brand-800">{stats.sugestoes_pendentes}</p>
            <p className="text-xs text-brand-500">{t('catalogo.stats.pendentes')}</p>
          </div>
        </div>
      )}

      {/* Catálogo + filtros */}
      <section className="rounded-lg border border-brand-200 bg-white p-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-brand-800">{t('catalogo.table.title')}</h2>
          <div className="flex gap-2">
            {/* Issue #568: CRUD direto — adicionar municipio/provedor ao
                catalogo (pagina exclusiva de super_user, acao incondicional). */}
            <button
              type="button"
              onClick={() => {
                setEditingIbge(null);
                setMunForm(EMPTY_MUN_FORM);
                setShowMunForm(true);
              }}
              className="rounded bg-brand-600 px-3 py-1 text-sm text-white hover:bg-brand-700"
            >
              {t('settings.providers.addButton')}
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="rounded border border-brand-300 px-3 py-1 text-sm text-brand-700 hover:bg-brand-50"
            >
              {t('catalogo.export')}
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="rounded border border-brand-300 px-3 py-1 text-sm text-brand-700 hover:bg-brand-50"
            >
              {t('catalogo.import')}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={handleImportFile}
            />
          </div>
        </div>
        <form onSubmit={applyFilters} className="mb-4 flex flex-wrap gap-2">
          <input
            className={inputCls}
            placeholder={t('catalogo.filter.uf')}
            value={filters.uf}
            maxLength={2}
            onChange={(e) => setFilters({ ...filters, uf: e.target.value })}
          />
          <input
            className={inputCls}
            placeholder={t('catalogo.filter.provedor')}
            value={filters.provedor}
            onChange={(e) => setFilters({ ...filters, provedor: e.target.value })}
          />
          <input
            className={inputCls}
            placeholder={t('catalogo.filter.busca')}
            value={filters.busca}
            onChange={(e) => setFilters({ ...filters, busca: e.target.value })}
          />
          <button
            type="submit"
            className="rounded bg-brand-600 px-3 py-1 text-sm text-white hover:bg-brand-700"
          >
            {t('common.filter')}
          </button>
        </form>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-brand-100">
                <th className={thCls}>{t('catalogo.table.ibge')}</th>
                <th className={thCls}>{t('catalogo.table.nome')}</th>
                <th className={thCls}>{t('catalogo.table.uf')}</th>
                <th className={thCls}>{t('catalogo.table.provedor')}</th>
                <th className={thCls}>{t('catalogo.table.agregador')}</th>
                {/* Issue #568: ações em linha (editar/excluir) — incondicional. */}
                <th className={thCls}>{t('catalogo.moderation.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {municipios.map((m) => (
                <tr key={m.id} className="border-b border-brand-50">
                  <td className={tdCls}>{m.codigo_ibge}</td>
                  <td className={tdCls}>{m.nome}</td>
                  <td className={tdCls}>{m.uf}</td>
                  <td className={tdCls}>{m.provedor}</td>
                  <td className={tdCls}>{m.agregador ?? t('common.dash')}</td>
                  <td className={tdCls}>
                    <span className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => startEditMunicipio(m)}
                        className="rounded border border-brand-300 px-2 py-1 text-xs text-brand-700 hover:bg-brand-50"
                      >
                        {t('settings.edit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteMunicipio(m)}
                        className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                      >
                        {t('settings.delete')}
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
              {municipios.length === 0 && (
                <tr>
                  <td className={tdCls} colSpan={6}>
                    {t('catalogo.table.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Sugerir mapeamento */}
      <section className="rounded-lg border border-brand-200 bg-white p-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-800">
          {t('catalogo.suggest.title')}
        </h2>
        <form onSubmit={submitSugestao} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.ibge')}
            <input
              required
              minLength={7}
              maxLength={7}
              className={inputCls}
              value={form.codigo_ibge}
              onChange={(e) => setForm({ ...form, codigo_ibge: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.nome')}
            <input
              required
              className={inputCls}
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.uf')}
            <input
              required
              minLength={2}
              maxLength={2}
              className={inputCls}
              value={form.uf}
              onChange={(e) => setForm({ ...form, uf: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.provedor')}
            <input
              required
              className={inputCls}
              value={form.provedor}
              onChange={(e) => setForm({ ...form, provedor: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.agregador')}
            <input
              className={inputCls}
              value={form.agregador}
              onChange={(e) => setForm({ ...form, agregador: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.justificativa')}
            <input
              className={inputCls}
              value={form.justificativa}
              onChange={(e) => setForm({ ...form, justificativa: e.target.value })}
            />
          </label>
          <div className="sm:col-span-3">
            <button
              type="submit"
              className="rounded bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700"
            >
              {t('catalogo.suggest.submit')}
            </button>
          </div>
        </form>
      </section>

      {/* Sugestões — moderadores veem todas as orgs (o backend filtra
          por role) com ações aprovar/rejeitar nas pendentes. */}
      <section className="rounded-lg border border-brand-200 bg-white p-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-800">
          {t('catalogo.sugestoes.title')}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-brand-100">
                <th className={thCls}>{t('catalogo.table.ibge')}</th>
                <th className={thCls}>{t('catalogo.table.nome')}</th>
                <th className={thCls}>{t('catalogo.table.uf')}</th>
                <th className={thCls}>{t('catalogo.table.provedor')}</th>
                <th className={thCls}>{t('common.status')}</th>
                <th className={thCls}>{t('catalogo.moderation.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sugestoes.map((s) => (
                <tr key={s.id} className="border-b border-brand-50">
                  <td className={tdCls}>{s.codigo_ibge}</td>
                  <td className={tdCls}>{s.nome}</td>
                  <td className={tdCls}>{s.uf}</td>
                  <td className={tdCls}>{s.provedor}</td>
                  <td className={tdCls}>
                    {t(`catalogo.sugestoes.status.${s.status}`)}
                  </td>
                  <td className={tdCls}>
                    {s.status === 'pendente' && (
                      <span className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleApprove(s.id)}
                          className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700"
                        >
                          {t('catalogo.moderation.approve')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReject(s.id)}
                          className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                        >
                          {t('catalogo.moderation.reject')}
                        </button>
                      </span>
                    )}
                    {s.status !== 'pendente' && (s.review_note ?? t('common.dash'))}
                  </td>
                </tr>
              ))}
              {sugestoes.length === 0 && (
                <tr>
                  <td className={tdCls} colSpan={6}>
                    {t('catalogo.sugestoes.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Histórico (versionamento) */}
      <section className="rounded-lg border border-brand-200 bg-white p-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-800">{t('catalogo.history.title')}</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-brand-100">
                <th className={thCls}>{t('catalogo.table.ibge')}</th>
                <th className={thCls}>{t('catalogo.history.action')}</th>
                <th className={thCls}>{t('common.origin')}</th>
                <th className={thCls}>{t('common.date')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-b border-brand-50">
                  <td className={tdCls}>{h.codigo_ibge}</td>
                  <td className={tdCls}>{t(`catalogo.history.action.${h.action}`)}</td>
                  <td className={tdCls}>{t(`catalogo.history.origem.${h.origem}`)}</td>
                  <td className={tdCls}>
                    {h.created_at ? new Date(h.created_at).toLocaleString() : t('common.dash')}
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr>
                  <td className={tdCls} colSpan={4}>
                    {t('catalogo.history.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Issue #568: modal criar/editar municipio — mesmo padrao do
          ProvidersCatalogTab (TenantSettings); campos IBGE + agregador. */}
      {showMunForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-brand-900">
              {editingIbge
                ? t('settings.providers.editTitle')
                : t('settings.providers.addTitle')}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.providers.col.ibge')}
                </label>
                <input
                  type="text"
                  value={munForm.codigo_ibge}
                  onChange={(e) => setMunForm({ ...munForm, codigo_ibge: e.target.value })}
                  disabled={editingIbge !== null}
                  maxLength={7}
                  className={`${inputCls} w-full disabled:bg-brand-50`}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.providers.col.name')}
                </label>
                <input
                  type="text"
                  value={munForm.nome}
                  onChange={(e) => setMunForm({ ...munForm, nome: e.target.value })}
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.providers.col.uf')}
                </label>
                <input
                  type="text"
                  value={munForm.uf}
                  onChange={(e) =>
                    setMunForm({ ...munForm, uf: e.target.value.toUpperCase() })
                  }
                  maxLength={2}
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.providers.col.provider')}
                </label>
                <input
                  type="text"
                  value={munForm.provedor}
                  onChange={(e) => setMunForm({ ...munForm, provedor: e.target.value })}
                  placeholder="GISS, GINFES, ABRASF..."
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.providers.col.aggregator')}
                </label>
                <input
                  type="text"
                  value={munForm.agregador}
                  onChange={(e) => setMunForm({ ...munForm, agregador: e.target.value })}
                  placeholder="PlugNotas, TecnoSpeed, NFe.io..."
                  className={`${inputCls} w-full`}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowMunForm(false);
                  setEditingIbge(null);
                }}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
              >
                {t('settings.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveMunicipio}
                disabled={busy || !canSaveMunicipio}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {busy ? t('settings.saving') : t('settings.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
